import { Link } from "react-router-dom";
import { ArrowRight, Star } from "lucide-react";
import { CASE_STUDIES } from "@/data/caseStudies";
import SEO from "@/components/SEO";
import NewsletterSection from "@/components/NewsletterSection";
import { T } from "@/lib/texts";

export default function Home() {
  const articles = CASE_STUDIES.slice(0, 3);

  return (
    <>
      <SEO
        path="/"
        title="Business Consultant & Brand Strategist per PMI e Aziende in Italia"
        description="Roberta Castagna — Consulente d'impresa e Brand Strategist. Reingegnerizzo processi produttivi e organizzativi di aziende italiane e PMI con il Metodo A.L.L.I.N.E.A. Consulenza in sede in tutta Italia, online o ibrida. Sede a Gravina di Catania, presidio su Sicilia, Roma e Firenze. Check-Up gratuito in 10 minuti."
      />
      {/* HERO */}
      <section data-testid="home-hero" className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 fade-up" data-reveal>
            <p className="eyebrow mb-6">Consulenza · Processi · Brand</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#2A3229]">
              Allineo la tua <span className="text-[#323C36]">macchina operativa</span> al valore del tuo <span className="text-[#A8674F]">Brand.</span>
            </h1>
            <p className="mt-4 font-display text-xl sm:text-2xl text-[#2A3229]/80 leading-snug">
              <T k="hero_subtitle">Per trasformare il caos gestionale in crescita scalabile.</T>
            </p>
            <p className="mt-8 max-w-xl text-lg leading-relaxed text-[#5A6058]">
              Sblocco il tempo del titolare d'azienda e reingegnerizzo i flussi interni delle aziende attraverso il Metodo proprietario <strong className="text-[#2A3229]">A.L.L.I.N.E.A.</strong>
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link data-testid="hero-cta-quiz" to="/checkup" className="btn-accent">
                Fai il Check‑Up <ArrowRight size={16} />
              </Link>
              <a data-testid="hero-cta-contact" href="https://calendar.app.google/wMsDARnKAyyi4doX6" target="_blank" rel="noreferrer" className="btn-secondary">
                Prenota una call
              </a>
            </div>
            <a
              data-testid="hero-google-badge"
              href="https://share.google/kOfk1mZ4wFh9qx5TN"
              target="_blank" rel="noreferrer"
              className="mt-8 inline-flex items-center gap-3 group"
            >
              <span className="flex items-center gap-1" aria-hidden="true">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={15} className="fill-[#E8A050] text-[#E8A050]" />
                ))}
              </span>
              <span className="text-sm text-[#5A6058] group-hover:text-[#2A3229] transition-colors">
                Recensioni dei clienti su <strong className="text-[#2A3229] font-semibold">Google</strong>
              </span>
              <ArrowRight size={14} className="text-[#A8674F] transition-transform group-hover:translate-x-1" />
            </a>
          </div>
          <div className="lg:col-span-6 relative" data-reveal data-reveal-delay="2">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                data-testid="hero-image"
                src="https://customer-assets.emergentagent.com/job_process-gap/artifacts/06tz6h9s_Foto%20Profilo.webp"
                alt="Roberta Castagna" className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#323C36]/25 via-transparent to-transparent" />
            </div>
            <div className="hidden lg:block absolute -bottom-8 -left-8 bg-[#F6F3EF] border border-[#323C36]/10 p-6 max-w-xs shadow-sm">
              <p className="eyebrow mb-2">Il mio approccio</p>
              <p className="font-display text-xl leading-snug text-[#2A3229]">"Un'azienda migliore parte sempre da domande migliori."</p>
            </div>
          </div>
        </div>
      </section>

      {/* SLOGAN */}
      <section data-testid="home-slogan" className="relative overflow-hidden bg-[#323C36] py-14 sm:py-16">
        <img
          src="https://customer-assets-v7afamib.emergentagent.net/job_process-gap/artifacts/eujhs0oi_STORIES%20CHALLENGE%20FEBBRAIO%20%286%29.png"
          alt=""
          aria-hidden="true"
          className="absolute -right-10 top-1/2 -translate-y-1/2 w-[420px] max-w-none opacity-[0.14] pointer-events-none select-none"
        />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center relative" data-reveal>
          <p className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#F6F3EF] leading-snug">
            <T k="slogan_text">Più ordine<span className="text-[#FAC075]">,</span> più margini<span className="text-[#FAC075]">,</span> zero sprechi<span className="text-[#FAC075]">.</span></T>
          </p>
        </div>
      </section>

      {/* IL PROBLEMA */}
      <section className="bg-[#EDE7DD] section-pad">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-16" data-reveal>
            <p className="eyebrow mb-4">Il Problema</p>
            <h2 className="font-display text-4xl sm:text-5xl leading-tight text-[#2A3229]">
              I sintomi che <span className="text-[#A8674F] whitespace-nowrap">conosco meglio di te</span>.
            </h2>
            <p className="mt-6 text-lg text-[#5A6058] leading-relaxed">
              Se ti riconosci in almeno uno di questi punti, sai già dove il tuo sistema sta perdendo margine, tempo e clienti.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { pillar: "Autonomia", q: "Il tuo fatturato dipende al 100% dal tuo carisma o dalla tua presenza fisica?" },
              { pillar: "Leadership", q: "Il tuo team esegue compiti ma non si prende responsabilità operative?" },
              { pillar: "Narrazione", q: "Hai un prodotto qualitativamente superiore al tuo competitor ma vendi meno?" },
            ].map((p, i) => (
              <div
                key={i}
                data-testid={`problem-${i}`}
                data-reveal
                data-reveal-delay={`${i + 1}`}
                className="bg-white border border-[#323C36]/10 p-8 transition-all duration-500 hover:-translate-y-1 hover:border-[#323C36] hover:shadow-lg"
              >
                <p className="text-xs uppercase tracking-[0.28em] font-semibold text-[#A8674F] mb-4">{p.pillar}</p>
                <p className="font-display text-xl leading-snug text-[#2A3229]">{p.q}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* IL METODO — Section 3 */}
      <section className="section-pad">
        <div className="max-w-5xl mx-auto px-6 lg:px-10" data-reveal>
          <p className="eyebrow mb-4">Il Mio Metodo</p>
          <h2 className="font-display text-4xl sm:text-5xl leading-tight text-[#2A3229] mb-8">
            Dall'Analisi Strategica all'<span className="text-[#323C36]">Asset di Vendita</span>.
          </h2>
          <div className="space-y-6 text-lg text-[#5A6058] leading-relaxed max-w-3xl">
            <p className="text-[#2A3229]"><strong>Non ti serve altra teoria. Ti serve un'infrastruttura aziendale che genera clienti e protegge il tuo tempo.</strong></p>
            <p>Per fare il salto di qualità sul mercato, la tua azienda non ha bisogno di consulenze astratte o di post improvvisati sui social. Ha bisogno di <strong className="text-[#323C36]">ingegneria strategica</strong>: sistemi replicabili, processi fluidi e strumenti proprietari capaci di produrre risultati costanti.</p>
          </div>
          <div className="mt-10">
            <Link to="/metodo" className="btn-primary" data-testid="home-see-method">Scopri il Metodo A.L.L.I.N.E.A.</Link>
          </div>
        </div>
      </section>

      {/* QUIZ CTA */}
      <section className="relative overflow-hidden bg-[#2A3229] text-[#F6F3EF] section-pad">
        <img
          src="https://customer-assets-v7afamib.emergentagent.net/job_process-gap/artifacts/qdz7148n_STORIES%20CHALLENGE%20FEBBRAIO%20%284%29.png"
          alt=""
          aria-hidden="true"
          className="absolute -right-20 -bottom-24 w-[420px] max-w-none opacity-[0.12] pointer-events-none select-none"
        />
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center relative" data-reveal>
          <p className="eyebrow !text-[#FAC075] mb-4">Check‑Up Aziendale</p>
          <h2 className="font-display text-4xl sm:text-5xl leading-tight">Dove si trova <em className="text-[#FAC075]">il gap</em> nella tua azienda?</h2>
          <p className="mt-6 text-lg text-[#F6F3EF]/75 max-w-2xl mx-auto">
            Dedica 10 minuti senza distrazioni a questa fotografia operativa, dalle tue risposte elaborerò un documento che mostra dove si trova in questo momento la tua azienda e quali sono gli elementi che stanno rallentando la tua crescita.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link data-testid="cta-quiz-1" to="/checkup" className="btn-accent">Accedi al Check‑Up</Link>
          </div>
        </div>
      </section>

      {/* BLOG PREVIEW */}
      <section className="bg-[#EDE7DD] section-pad">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4" data-reveal>
            <div>
              <p className="eyebrow mb-3">Casi Studio</p>
              <h2 className="font-display text-4xl sm:text-5xl text-[#2A3229]">Storie per <span className="text-[#323C36]">imprenditori</span></h2>
            </div>
            <Link data-testid="home-see-blog" to="/casistudio" className="text-[#323C36] link-underline text-sm uppercase tracking-widest">Vedi tutti →</Link>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {articles.map((a, i) => (
              <Link
                data-testid={`blog-card-${a.id}`}
                to={`/casistudio/${a.slug}`}
                key={a.id}
                data-reveal
                data-reveal-delay={`${i + 1}`}
                className="group flex flex-col"
              >
                <div className="aspect-[4/3] overflow-hidden mb-4">
                  <img src={a.cover} alt={a.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <p className="eyebrow mb-2">{a.category}</p>
                <h3 className="font-display text-xl leading-snug text-[#2A3229] group-hover:text-[#323C36] transition-colors mb-4">{a.title}</h3>
                {a.hook && (
                  <div className="bg-white border border-[#323C36]/10 p-4 mt-auto">
                    <div className="flex items-start gap-2 text-sm">
                      <span className="text-[#A8674F] font-semibold shrink-0">DA</span>
                      <span className="text-[#5A6058]">{a.hook.from}</span>
                    </div>
                    <div className="flex items-center justify-center my-1"><ArrowRight size={14} className="text-[#A8674F] rotate-90"/></div>
                    <div className="flex items-start gap-2 text-sm">
                      <span className="text-[#323C36] font-semibold shrink-0">A</span>
                      <span className="text-[#2A3229] font-medium">{a.hook.to}</span>
                    </div>
                  </div>
                )}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <NewsletterSection />
    </>
  );
}
